import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * UpgradeConfirmModal — confirmation dialog rendered before an in-place
 * subscription upgrade. Shows the prorated charge, line breakdown, the
 * recurring next-billing amount, and (for trialing users) a warning that
 * confirming will end their trial immediately.
 *
 * Designed for use INSIDE PricingPanel — but exported standalone for hosts
 * that want to compose their own upgrade flow.
 *
 * Accessibility:
 *   - role="dialog" + aria-modal="true"
 *   - aria-labelledby points at the title
 *   - aria-describedby points at the amount
 *   - ESC key closes (calls onCancel)
 *   - Backdrop click closes (calls onCancel)
 *   - Focus moves to Cancel button on open
 *   - Focus restoration handled by the parent (PricingPanel captures
 *     document.activeElement BEFORE opening the modal)
 *
 * See HD-81 design doc § 6.
 */
import { useEffect, useId, useRef } from "react";
import { formatPrice } from "../helpers.js";
import { ErrorBanner } from "./ErrorBanner.js";
import { LoadingSkeleton } from "./LoadingSkeleton.js";
// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
export function UpgradeConfirmModal(props) {
    const { isOpen, preview, isPreviewLoading, isConfirming, error, targetLabel, onConfirm, onCancel, } = props;
    // HD-119: a scheduled downgrade (annual->monthly or tier-down) previews as
    // mutation "D" — €0 now, effective at period end. Show "switches on <date>"
    // instead of the charge-now copy, and a neutral confirm label.
    const scheduled = preview?.mutation === "D";
    const titleId = useId();
    const amountId = useId();
    const cancelButtonRef = useRef(null);
    const dialogRef = useRef(null);
    // ESC key closes
    useEffect(() => {
        if (!isOpen)
            return;
        function onKeyDown(e) {
            if (e.key === "Escape")
                onCancel();
        }
        document.addEventListener("keydown", onKeyDown);
        return () => document.removeEventListener("keydown", onKeyDown);
    }, [isOpen, onCancel]);
    // Focus the Cancel button on open (less destructive default)
    useEffect(() => {
        if (!isOpen)
            return;
        // Defer to next tick so the element is mounted and visible
        const t = setTimeout(() => cancelButtonRef.current?.focus(), 0);
        return () => clearTimeout(t);
    }, [isOpen]);
    if (!isOpen)
        return null;
    // Backdrop click handler — only triggers when the click target IS the backdrop,
    // not when the click bubbled up from inside the dialog.
    function onBackdropClick(e) {
        if (e.target === e.currentTarget)
            onCancel();
    }
    return (_jsx("div", { className: "pp-modal__overlay", onClick: onBackdropClick, "data-testid": "pp-modal-overlay", children: _jsxs("div", { ref: dialogRef, className: "pp-modal__dialog", role: "dialog", "aria-modal": "true", "aria-labelledby": titleId, "aria-describedby": amountId, children: [_jsx("h2", { id: titleId, className: "pp-modal__title", children: targetLabel }), preview?.willEndTrial && (_jsx("div", { role: "alert", className: "pp-modal__warning", children: "Your free trial will end immediately when you confirm." })), isPreviewLoading && !preview && _jsx(LoadingSkeleton, {}), preview && scheduled && (_jsxs("div", { id: amountId, className: "pp-modal__amount", children: [_jsx("strong", { children: "No charge now." }), " Your plan switches on", " ", _jsx("strong", { children: formatNextBillingDate(preview.nextBillingDate) }), ". You keep your current plan until then."] })), preview && !scheduled && (_jsxs(_Fragment, { children: [_jsxs("div", { id: amountId, className: "pp-modal__amount", children: ["You'll be charged", " ", _jsx("strong", { children: formatPrice(preview.amountDueCents) }), " now."] }), preview.lineItems.length > 0 && (_jsx("ul", { className: "pp-modal__lines", children: preview.lineItems.map((line, i) => (_jsxs("li", { children: [_jsx("span", { children: line.description ?? "Adjustment" }), _jsx("span", { children: formatSignedPrice(line.amountCents) })] }, i))) })), preview.nextBillingAmountCents > 0 && (_jsxs("div", { className: "pp-modal__next", children: ["Then", " ", _jsx("strong", { children: formatPrice(preview.nextBillingAmountCents) }), " ", "starting", " ", _jsx("strong", { children: formatNextBillingDate(preview.nextBillingDate) }), "."] }))] })), error && _jsx(ErrorBanner, { message: error, onDismiss: () => { } }), _jsxs("div", { className: "pp-modal__btns", children: [_jsx("button", { ref: cancelButtonRef, type: "button", className: "pp-modal__btn pp-modal__btn--cancel", onClick: onCancel, disabled: isConfirming, children: "Cancel" }), _jsx("button", { type: "button", className: "pp-modal__btn pp-modal__btn--confirm", onClick: onConfirm, disabled: isConfirming || !preview, "aria-busy": isConfirming, children: isConfirming ? "Confirming..." : scheduled ? "Confirm change" : "Confirm upgrade" })] })] }) }));
}
// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
/** Format a signed cent amount with a leading sign (e.g. "+$5.18", "−$3.42"). */
function formatSignedPrice(cents) {
    if (cents === 0)
        return formatPrice(0);
    const abs = formatPrice(Math.abs(cents));
    return cents > 0 ? `+${abs}` : `−${abs}`;
}
/** Format a unix-ms timestamp using the browser's locale (HD-81 § 9 C7). */
function formatNextBillingDate(ms) {
    const fmt = new Intl.DateTimeFormat(undefined, {
        month: "long",
        day: "numeric",
        year: "numeric",
    });
    return fmt.format(new Date(ms));
}
